import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { cn } from "../../lib/utils";

interface LinkProps extends React.HTMLAttributes<HTMLAnchorElement> {
  href?: string;
  to?: string;
  onClick?: (e: React.MouseEvent) => void;
  variant?: "default" | "inherit";
  children: React.ReactNode;
  asChild?: boolean;
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      href,
      to,
      onClick,
      variant = "default",
      asChild,
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses = asChild
      ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      : "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

    const variantClasses = {
      default: asChild ? "" : "text-primary underline-offset-4 hover:underline",
      inherit: asChild
        ? ""
        : "text-inherit underline underline-offset-3 hover:underline hover:text-primary",
    };

    const classes = cn(baseClasses, variantClasses[variant], className);

    // If it's a navigation link (has 'to' prop)
    if (to) {
      return (
        <RouterLink ref={ref} to={to} className={classes} {...props}>
          {children}
        </RouterLink>
      );
    }

    // If it's a regular href link
    if (href) {
      return (
        <a ref={ref} href={href} className={classes} {...props}>
          {children}
        </a>
      );
    }

    // If it's a clickable element
    return (
      <span
        ref={ref}
        className={classes}
        onClick={onClick}
        style={{ cursor: onClick ? "pointer" : "inherit" }}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Link.displayName = "Link";

export { Link };
